'use client';

import { useMemo } from 'react';

interface TextSegment {
  text: string;
  match: boolean;
}

const removeDiacritics = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const buildSearchIndex = (text: string) => {
  const starts: number[] = [];
  const ends: number[] = [];
  let normalized = '';

  let i = 0;
  for (const char of text) {
    const start = i;
    i += char.length;
    const end = i;

    const normalizedChar = removeDiacritics(char);
    for (let j = 0; j < normalizedChar.length; j += 1) {
      const c = normalizedChar[j];
      normalized += c;
      starts.push(start);
      ends.push(end);
    }
  }

  return { normalized, starts, ends };
};

export const splitTextByQuery = (text: string, query: string): TextSegment[] => {
  const normalizedText = text || '';
  const normalizedQuery = query.trim();

  if (!normalizedText) {
    return [{ text: '', match: false }];
  }

  if (!normalizedQuery) {
    return [{ text: normalizedText, match: false }];
  }

  const { normalized: searchableText, starts, ends } = buildSearchIndex(normalizedText);
  const lowerText = searchableText.toLowerCase();
  const lowerQuery = removeDiacritics(normalizedQuery).toLowerCase();
  const segments: TextSegment[] = [];

  let currentOriginalIndex = 0;
  let startIndex = 0;
  while (startIndex < lowerText.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, startIndex);

    if (matchIndex === -1) {
      segments.push({ text: normalizedText.slice(currentOriginalIndex), match: false });
      break;
    }

    const matchStart = starts[matchIndex];
    const matchEnd = ends[matchIndex + lowerQuery.length - 1];

    if (matchStart > currentOriginalIndex) {
      segments.push({ text: normalizedText.slice(currentOriginalIndex, matchStart), match: false });
    }

    segments.push({ text: normalizedText.slice(matchStart, matchEnd), match: true });
    currentOriginalIndex = matchEnd;
    startIndex = matchIndex + lowerQuery.length;
  }

  return segments;
};

interface HighlightedTextProps {
  text: string;
  query: string;
}

export const HighlightedText = ({ text, query }: HighlightedTextProps) => {
  const segments = useMemo(() => splitTextByQuery(text, query), [text, query]);

  return (
    <>
      {segments.map((segment, idx) => {
        if (!segment.match) {
          return <span key={idx}>{segment.text}</span>;
        }

        return (
          <span
            key={idx}
            className="border-warning-100 bg-warning-100 text-warning-800 rounded-sm border px-0.5"
          >
            {segment.text}
          </span>
        );
      })}
    </>
  );
};
